import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  split,
  from,
  Observable,
  Operation,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { captureException } from "@sentry/react";
import {
  getGraphQLAuthURL,
  getWebsocketAuthURL,
  SUBCONTRACTING_FOR_USER,
  TOKEN_KEY,
} from "@/env.ts";
import get from "lodash/get";
import { HOME_URL } from "@/helpers/url.ts";
import { getMainDefinition } from "@apollo/client/utilities";
import { OperationDefinitionNode } from "graphql/language";
import history from "@/helpers/history.ts";
import { WebSocketLink } from "@apollo/client/link/ws";
import { SubscriptionClient } from "subscriptions-transport-ws";

const httpLink = new HttpLink({
  uri: getGraphQLAuthURL(),
});

const wsClient = new SubscriptionClient(
  `${getWebsocketAuthURL()}?token=${localStorage.getItem(TOKEN_KEY)}`,
  {
    reconnect: true,
  },
);

const wsLink = new WebSocketLink(wsClient);

const link = split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(
      query,
    ) as OperationDefinitionNode;
    return kind === "OperationDefinition" && operation === "subscription";
  },
  wsLink,
  httpLink,
);

const request = async (operation: Operation) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    operation.setContext({
      headers: {
        Authorization: `Bearer ${token}`,
        "x-subcontracting-for-user": localStorage.getItem(
          SUBCONTRACTING_FOR_USER,
        ),
      },
    });
  }
};

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle: { unsubscribe: () => void } | null = null;
      Promise.resolve(operation)
        .then((oper) => request(oper))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    }),
);

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (networkError) {
    if (operation.operationName === "CurrentUserQuery") {
      return;
    }

    captureException(networkError);
    console.warn(`[Network error]: ${networkError}`);

    if (get(networkError, "statusCode") === 401) {
      localStorage.removeItem(TOKEN_KEY);
      history.push(HOME_URL);
    }
  }

  if (graphQLErrors) {
    graphQLErrors.forEach((graphqlError) => {
      if (typeof graphqlError === "string") {
        console.warn(`[GraphQL error]: Message: ${graphqlError}`);
      } else {
        const { message, locations, path } = graphqlError;
        console.warn(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      }
    });
  }
});

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, requestLink, link]),
});
